/**
 * Aviso de copia (Rumbo): si alguien copia la web tal cual y la sirve desde
 * otro dominio (p. ej. para robar contraseñas), la app no arranca y enseña un
 * aviso a pantalla completa que manda a la Rumbo original. Va en castellano,
 * catalán e inglés a la vez porque quien cae en una copia puede ser cualquiera.
 * No frena a quien reescribe la app desde cero: solo a las copias literales.
 */

export const DOMINIO_RUMBO = 'rumbofinanciero.es';

const URL_ORIGINAL = `https://${DOMINIO_RUMBO}`;

/** Direcciones de desarrollo y pruebas: el propio equipo o su red de casa. */
function esDireccionLocal(host: string) {
  if (host === 'localhost' || host.endsWith('.localhost')) {
    return true;
  }
  if (host === '[::1]' || host === '::1') {
    return true;
  }
  const partes = host.split('.').map(Number);
  if (partes.length !== 4 || partes.some(p => !Number.isInteger(p))) {
    return false;
  }
  const [a, b] = partes;
  return (
    a === 127 ||
    a === 10 ||
    (a === 192 && b === 168) ||
    (a === 172 && b >= 16 && b <= 31)
  );
}

/** ¿Esta dirección es Rumbo de verdad (o un entorno de desarrollo)? */
export function esOrigenRumbo(hostname: string) {
  const host = hostname.toLowerCase().replace(/\.$/, '');
  return (
    host === DOMINIO_RUMBO ||
    host.endsWith(`.${DOMINIO_RUMBO}`) ||
    esDireccionLocal(host)
  );
}

type TextoAviso = {
  idioma: 'es' | 'ca' | 'en';
  titulo: string;
  cuerpo: string;
  boton: string;
};

export const TEXTOS_AVISO: TextoAviso[] = [
  {
    idioma: 'es',
    titulo: 'Esto no es Rumbo.',
    cuerpo:
      'Estás en una copia pirata de Rumbo Financiero. Quien la ha montado puede ver todo lo que escribas aquí: tu contraseña, tus cuentas y tus movimientos. No escribas nada. Cierra esta página y entra en la Rumbo original.',
    boton: `Ir a ${DOMINIO_RUMBO}`,
  },
  {
    idioma: 'ca',
    titulo: 'Això no és Rumbo.',
    cuerpo:
      "Ets en una còpia pirata de Rumbo Financiero. Qui l'ha muntada pot veure tot el que escriguis aquí: la teva contrasenya, els teus comptes i els teus moviments. No hi escriguis res. Tanca aquesta pàgina i entra a la Rumbo original.",
    boton: `Ves a ${DOMINIO_RUMBO}`,
  },
  {
    idioma: 'en',
    titulo: 'This is not Rumbo.',
    cuerpo:
      "You are on a pirated copy of Rumbo Financiero. Whoever set it up can see everything you type here: your password, your accounts and your transactions. Don't type anything. Close this page and go to the original Rumbo.",
    boton: `Go to ${DOMINIO_RUMBO}`,
  },
];

/** Los tres textos, con el idioma del navegador primero. */
export function ordenarTextos(idiomasNavegador: readonly string[]) {
  const preferido = idiomasNavegador
    .map(idioma => idioma.toLowerCase().slice(0, 2))
    .find(idioma => TEXTOS_AVISO.some(texto => texto.idioma === idioma));
  if (!preferido) {
    return TEXTOS_AVISO;
  }
  return [
    ...TEXTOS_AVISO.filter(texto => texto.idioma === preferido),
    ...TEXTOS_AVISO.filter(texto => texto.idioma !== preferido),
  ];
}

/** Sustituye la página entera por el aviso. Sin React: la app no llega a arrancar. */
export function mostrarAvisoCopia(documento: Document) {
  const idiomas = documento.defaultView?.navigator.languages ?? [];
  const aviso = documento.createElement('main');
  aviso.setAttribute('role', 'alert');
  aviso.setAttribute('data-aviso-copia', '');
  aviso.style.cssText = [
    'position:fixed',
    'inset:0',
    'overflow:auto',
    'padding:32px 16px',
    'background:#0b1f33',
    'color:#fdf6e3',
    'font:16px/1.5 system-ui,sans-serif',
    'z-index:2147483647',
  ].join(';');

  for (const texto of ordenarTextos(idiomas)) {
    const bloque = documento.createElement('section');
    bloque.lang = texto.idioma;
    bloque.style.cssText = 'max-width:480px;margin:0 auto 32px';

    const titulo = documento.createElement('h1');
    titulo.textContent = `⚠️ ${texto.titulo}`;
    titulo.style.cssText = 'font-size:26px;margin:0 0 12px;color:#ffb4a2';

    const cuerpo = documento.createElement('p');
    cuerpo.textContent = texto.cuerpo;
    cuerpo.style.cssText = 'margin:0 0 16px';

    const enlace = documento.createElement('a');
    enlace.href = URL_ORIGINAL;
    enlace.rel = 'noopener';
    enlace.textContent = texto.boton;
    enlace.style.cssText =
      'display:inline-block;padding:12px 20px;border-radius:12px;background:#e0b04a;color:#0b1f33;font-weight:700;text-decoration:none';

    bloque.append(titulo, cuerpo, enlace);
    aviso.append(bloque);
  }

  documento.body.replaceChildren(aviso);
  documento.title = TEXTOS_AVISO[0].titulo;
}
